import {useEffect,useRef,useState} from 'react';
import {useHermes} from './lib/store';
import {APP_BASE,Link,navigate} from './router';
import {ErrorNotice,Loading,PageHeading,dateTime} from './components';
import type {MessageFeed} from '../src/account-message-types';
import './account-messages.css';

function MessageList({feed}:{feed:MessageFeed}) {
  return <ol className="account-message-list">{feed.items.map(message=><li key={message.id} data-sender={message.sender}>
    <div className="account-message-meta"><span>{message.sender==='user'?'Account message':message.sender==='admin'?'EightBots support':'Account notification'}</span>
      <time dateTime={message.occurredAt}>{dateTime(message.occurredAt)}</time>{!message.read&&<span className="message-unread">Unread</span>}</div>
    <h3>{message.title}</h3><p>{message.body}</p>
  </li>)}</ol>;
}

function useMessages(accountId:string|null,search:string) {
  const {state,actions}=useHermes();
  useEffect(()=>{
    let pending=false,stopped=false;
    const read=()=>{if(pending||document.hidden||stopped)return;pending=true;void actions.loadMessages(accountId,search).catch(()=>undefined).finally(()=>{pending=false;});};
    read();const timer=setInterval(read,30_000);document.addEventListener('visibilitychange',read);
    return()=>{stopped=true;clearInterval(timer);document.removeEventListener('visibilitychange',read);};
  },[actions,accountId,search,state.session.data?.accountId]);
  return {state,actions,resource:accountId?state.adminMessages:state.accountMessages};
}

export function AllowanceHistory() {
  const {resource}=useMessages(null,'category=allowance');
  return <section className="billing-section"><h2>Allowance history</h2>
    <Loading active={!resource.data&&resource.status==='loading'}/><ErrorNotice error={resource.error}/>
    {resource.data&&<>{resource.data.items.some(item=>item.category==='allowance')?<MessageList feed={{...resource.data,items:resource.data.items.filter(item=>item.category==='allowance')}}/>:<p>No weekly resets have been recorded yet.</p>}
      <Link href={`${APP_BASE}/messages?category=allowance`}>View all allowance notifications</Link></>}
  </section>;
}

export function MessagesPage({accountId=null,search=''}:{accountId?:string|null;search?:string}) {
  const query=new URLSearchParams([...new URLSearchParams(search)].filter(([key])=>['category','before'].includes(key)));
  const feedSearch=query.toString();
  const {state,actions,resource}=useMessages(accountId,feedSearch);
  const [draft,setDraft]=useState('');const request=useRef({id:crypto.randomUUID(),body:''});
  const busy=state.operation.label!==null,feed=resource.data;
  const path=accountId?'/admin/messages':'/messages';
  const href=(filter:string,before?:string)=>{
    const values=new URLSearchParams();if(accountId)values.set('accountId',accountId);if(filter)values.set('category',filter);if(before)values.set('before',before);
    return `${APP_BASE}${path}${values.size?'?'+values:''}`;
  };
  const selected=query.get('category')??'';
  return <section className="messages-page">
    <PageHeading title={accountId?`Messages${feed?.accountId===accountId?' with '+feed.profileName:''}`:'Messages'}
      action={accountId?<Link className="button button-quiet" href={`${APP_BASE}/admin/messages`}>All conversations</Link>:undefined}>
      {accountId?'Read account notifications and reply to this user.':'Contact EightBots and read your account notifications.'}
    </PageHeading>
    <div className="messages-layout"><aside className="messages-tools"><nav aria-label="Message filters">
      {[['','All messages'],['conversation','Conversations'],['allowance','Credit resets'],['account','Account updates']].map(([value,label])=>
        <Link key={value} href={href(value!)} aria-current={selected===value?'page':undefined}>{label}</Link>)}
      {!accountId&&<Link href={`${APP_BASE}/billing`}>Usage &amp; Billing</Link>}
    </nav><p>Messages stay in your account. Replies appear here.</p></aside>
    <div className="messages-content">
      <form className="account-message-compose" onSubmit={event=>{
        event.preventDefault();const body=draft.trim();if(!body||busy)return;
        if(request.current.body!==body)request.current={id:crypto.randomUUID(),body};
        void actions.sendAccountMessage(accountId,{requestId:request.current.id,body}).then(()=>{
          setDraft('');request.current={id:crypto.randomUUID(),body:''};navigate(href(''));
        }).catch(()=>undefined);
      }}><label htmlFor="account-message-draft">{accountId?'Message this user':'Message EightBots support'}</label>
        <textarea id="account-message-draft" value={draft} onChange={event=>setDraft(event.target.value)} rows={4} maxLength={8000} required disabled={busy}/>
        <div><span>{draft.length.toLocaleString()} / 8,000</span><button className="button" type="submit" disabled={busy||!draft.trim()}>Send message</button></div>
      </form>
      <Loading active={!feed&&(resource.status==='idle'||resource.status==='loading')}/><ErrorNotice error={resource.error}/>
      {feed&&(!accountId||feed.accountId===accountId)&&<><div className="messages-read-actions"><span>{feed.unread?`${feed.unread} unread`:'All caught up'}</span>
        {!!feed.unread&&feed.latestId&&<button className="text-button" disabled={busy} onClick={()=>void actions.readAccountMessages(accountId,feed.latestId!,feedSearch).catch(()=>undefined)}>Mark all as read</button>}</div>
        {feed.items.length?<MessageList feed={feed}/>:<p className="messages-empty">No messages in this view.</p>}
        <div className="messages-pagination">{query.has('before')&&<Link href={href(selected)}>Latest messages</Link>}
          {feed.next&&<Link href={href(selected,feed.next)}>Older messages</Link>}</div>
      </>}
    </div></div>
  </section>;
}

export function AdminMessageInbox({search}:{search:string}) {
  const {state,actions}=useHermes(),query=new URLSearchParams(search),accountId=query.get('accountId');
  const target=accountId;
  useEffect(()=>{
    if(target)return;let pending=false;
    const read=()=>{if(pending||document.hidden)return;pending=true;void actions.loadMessageInbox(search).catch(()=>undefined).finally(()=>{pending=false;});};
    read();const timer=setInterval(read,30_000);return()=>clearInterval(timer);
  },[actions,search,target]);
  if(target)return <MessagesPage key={target} accountId={target} search={new URLSearchParams([...query].filter(([key])=>key!=='accountId')).toString()}/>;
  const resource=state.adminMessageInbox;
  return <section className="messages-inbox"><form className="message-account-search" key={search} onSubmit={event=>{
    event.preventDefault();navigate(`${APP_BASE}/admin/messages?q=${encodeURIComponent(String(new FormData(event.currentTarget).get('q')??''))}`);
  }}><label className="field">Find an account<input name="q" type="search" defaultValue={query.get('q')??''} placeholder="Profile name or email" maxLength={150}/></label><button className="button">Search</button></form>
    <Loading active={!resource.data&&resource.status==='loading'}/><ErrorNotice error={resource.error}/>
    {resource.data&&<>{!resource.data.items.length&&<p>No conversations found. Search for an account to send its first message.</p>}
      <ol className="message-inbox-list">{resource.data.items.map(row=><li key={row.accountId}><div><Link href={`${APP_BASE}/admin/messages?accountId=${row.accountId}`}>{row.profileName}</Link>
        <span>{row.email}</span></div><p>{row.preview??'No conversation yet.'}</p><div><span>{row.updatedAt?dateTime(row.updatedAt):''}</span>{row.unread>0&&<strong>{row.unread} unread</strong>}</div></li>)}</ol>
      <div className="messages-pagination">{query.has('before')&&<Link href={`${APP_BASE}/admin/messages?q=${encodeURIComponent(query.get('q')??'')}`}>Latest conversations</Link>}
        {resource.data.next&&<Link href={`${APP_BASE}/admin/messages?q=${encodeURIComponent(query.get('q')??'')}&before=${resource.data.next.before}&cursorAccountId=${resource.data.next.accountId}`}>Older conversations</Link>}</div></>}
  </section>;
}
